import { useRef, useState } from "react";
import { useLang } from "../../../shared/i18n/useLang";
import { comprimirImagen, pesoLegible } from "../../../shared/utils/imagen";
import styles from "./tickets.module.css";

/**
 * Piezas compartidas entre la pantalla del residente (PortalTickets) y la de
 * la administración (AdminTickets): el estado, el hilo de mensajes y el
 * selector de adjuntos. El hilo se ve igual de los dos lados, lo único que
 * cambia es qué mensajes salen como propios.
 */

// Mismo orden y mismos nombres que Ticket.ESTADO_CHOICES en el backend.
export const ESTADOS = ["ABIERTO", "EN_PROCESO", "RESUELTO", "CERRADO"];

export const ICONO_ESTADO = {
  ABIERTO:    "fa-circle-dot",
  EN_PROCESO: "fa-spinner",
  RESUELTO:   "fa-circle-check",
  CERRADO:    "fa-lock",
};

/** Nombre del estado en el idioma activo. */
export function useEstadoLabel() {
  const L = useLang();
  const nombres = {
    ABIERTO:    L("Abierto", "Open"),
    EN_PROCESO: L("En proceso", "In progress"),
    RESUELTO:   L("Resuelto", "Resolved"),
    CERRADO:    L("Cerrado", "Closed"),
  };
  return (estado) => nombres[estado] || estado;
}

/**
 * Clase con el color del estado: define --acc, --acc-rgb y --acc-text, que es
 * de donde sacan el color la pastilla, las fichas y las filas de la tabla.
 * Un estado desconocido cae en el gris de CERRADO para no quedar sin color.
 */
export function acento(estado) {
  return styles[`acc${estado}`] || styles.accCERRADO;
}

export function EstadoBadge({ estado }) {
  const label = useEstadoLabel();
  return (
    <span className={`${styles.badge} ${acento(estado)}`}>
      <i className={`fa-solid ${ICONO_ESTADO[estado] || "fa-circle"}`} />
      {label(estado)}
    </span>
  );
}

/** Fecha corta con la hora, que es lo que importa en un hilo del mismo día. */
export function fechaHora(iso, idiomaEn = false) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(idiomaEn ? "en-US" : "es-CR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

/** Los adjuntos de un mensaje, como enlaces para abrirlos. */
function Adjuntos({ archivos }) {
  if (!archivos?.length) return null;
  return (
    <div>
      {archivos.map((a) => (
        <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className={styles.enlaceAdjunto}>
          <i className="fa-solid fa-paperclip" /> {a.nombre}
        </a>
      ))}
    </div>
  );
}

/**
 * El hilo completo. `esAdmin` dice de qué lado se está mirando: los mensajes
 * de ese lado salen como propios (a la derecha, en naranja).
 */
export function TicketHilo({ mensajes, esAdmin }) {
  const L = useLang();
  const label = useEstadoLabel();
  const en = L("es", "en") === "en";

  if (!mensajes?.length) {
    return (
      <div className={styles.vacio}>
        <i className={`fa-solid fa-comments ${styles.vacioIcono}`} />
        <p>{L("Todavía no hay mensajes.", "No messages yet.")}</p>
      </div>
    );
  }

  return (
    <div className={styles.hilo}>
      {mensajes.map((m) => {
        // Un cambio de estado sin comentario se lee como bitácora, centrado,
        // en vez de como una burbuja vacía de alguien.
        if (m.estado_nuevo && !m.cuerpo) {
          return (
            <div key={m.id} className={`${styles.burbujaFila} ${styles.sistemaFila}`}>
              <span className={`${styles.sistema} ${acento(m.estado_nuevo)}`}>
                <i className={`fa-solid ${ICONO_ESTADO[m.estado_nuevo] || "fa-circle"}`} />
                {L("Pasó a", "Changed to")} <strong>{label(m.estado_nuevo)}</strong>
                <span className={styles.separador}>·</span>
                {fechaHora(m.created_at, en)}
              </span>
            </div>
          );
        }
        const propia = !!m.es_admin === !!esAdmin;
        return (
          <div key={m.id} className={`${styles.burbujaFila} ${propia ? styles.burbujaFilaPropia : ""}`}>
            <div className={`${styles.burbuja} ${propia ? styles.burbujaPropia : ""}`}>
              <div className={styles.burbujaAutor}>
                <span className={styles.autorNombre}>{m.autor}</span>
                {m.es_admin && (
                  <span className={styles.autorRol}>{L("Administración", "Management")}</span>
                )}
                <span className={styles.burbujaFecha}>{fechaHora(m.created_at, en)}</span>
              </div>
              {m.cuerpo && <p className={styles.burbujaCuerpo}>{m.cuerpo}</p>}
              {m.estado_nuevo && (
                <span className={`${styles.sistema} ${acento(m.estado_nuevo)}`}>
                  <i className={`fa-solid ${ICONO_ESTADO[m.estado_nuevo] || "fa-circle"}`} />
                  {L("Pasó a", "Changed to")} <strong>{label(m.estado_nuevo)}</strong>
                </span>
              )}
              <Adjuntos archivos={m.archivos} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Mismos topes que valida el backend (core/archivos.py: validar_adjuntos_ticket).
export const MAX_ADJUNTOS = 3;
export const MAX_MB_ADJUNTO = 10;
const EXT_ADJUNTO = [".pdf", ".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif"];

/**
 * Selector de adjuntos con la compresión de imagen del módulo de Agua: la foto
 * de un celular pesa 3 a 6 MB y así viaja en unos 250 KB, que es lo que evitó
 * el 413 en las lecturas de agua.
 */
export function SelectorAdjuntos({ archivos, onChange, disabled }) {
  const L = useLang();
  const inputRef = useRef();
  const [error, setError] = useState("");
  const [procesando, setProcesando] = useState(false);

  async function agregar(e) {
    const nuevos = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = "";
    if (!nuevos.length) return;
    setError("");

    if (archivos.length + nuevos.length > MAX_ADJUNTOS) {
      setError(L(
        `Podés adjuntar hasta ${MAX_ADJUNTOS} archivos por mensaje.`,
        `You can attach up to ${MAX_ADJUNTOS} files per message.`,
      ));
      return;
    }

    setProcesando(true);
    const listos = [];
    for (const archivo of nuevos) {
      const punto = archivo.name.lastIndexOf(".");
      const ext = punto >= 0 ? archivo.name.slice(punto).toLowerCase() : "";
      if (!EXT_ADJUNTO.includes(ext)) {
        setError(L(
          `"${archivo.name}": adjuntá un PDF o una imagen.`,
          `"${archivo.name}": attach a PDF or an image.`,
        ));
        continue;
      }
      let final = archivo;
      if (archivo.type.startsWith("image/")) {
        try {
          final = await comprimirImagen(archivo);
        } catch {
          final = archivo;   // si el navegador no pudo, se sube tal cual
        }
      }
      if (final.size > MAX_MB_ADJUNTO * 1024 * 1024) {
        setError(L(
          `"${archivo.name}" pesa ${pesoLegible(final.size)} y el máximo es ${MAX_MB_ADJUNTO} MB.`,
          `"${archivo.name}" is ${pesoLegible(final.size)} and the limit is ${MAX_MB_ADJUNTO} MB.`,
        ));
        continue;
      }
      listos.push(final);
    }
    setProcesando(false);
    if (listos.length) onChange([...archivos, ...listos]);
  }

  function quitar(i) {
    setError("");
    onChange(archivos.filter((_, idx) => idx !== i));
  }

  return (
    <div>
      <button
        type="button"
        className={styles.btn}
        disabled={disabled || procesando || archivos.length >= MAX_ADJUNTOS}
        onClick={() => inputRef.current?.click()}
      >
        <i className="fa-solid fa-paperclip" />
        {procesando
          ? L("Preparando...", "Preparing...")
          : L("Adjuntar archivo", "Attach file")}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,.pdf"
        hidden
        onChange={agregar}
      />
      {!!archivos.length && (
        <div className={styles.adjuntos}>
          {archivos.map((a, i) => (
            <div key={`${a.name}-${i}`} className={styles.adjunto}>
              <i className="fa-solid fa-file" />
              <span className={styles.adjuntoNombre}>{a.name}</span>
              <span className={styles.adjuntoPeso}>{pesoLegible(a.size)}</span>
              <button
                type="button"
                className={styles.btnIcono}
                onClick={() => quitar(i)}
                aria-label={L("Quitar", "Remove")}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          ))}
        </div>
      )}
      {error && <p className={styles.ayuda}>{error}</p>}
    </div>
  );
}
